// Meta Business Agent ("Meta AI") wire contract shared by backend and frontend.
// Zod is the single source: the types are inferred from the schemas, and the
// SAME schemas validate the chatbot file save (code editor, frontend + backend)
// and the deploy-time zip parse — so the shapes can never drift apart.
import * as z from 'zod';

// Behavioural settings that ARE part of the versioned config. `rollout` is
// deliberately NOT here — it is the per-business publish/unpublish state
// (Activate/Deactivate), managed at runtime, never stored in the file. Meta
// owns and extends these payloads, so every level keeps unknown keys
// (`catchall`) instead of rejecting or stripping them.
export const metaAgentSettingsSchema = z
  .object({
    handoff: z
      .object({
        enabled: z.boolean().optional(),
        message: z.string().optional(),
      })
      .catchall(z.unknown())
      .optional(),
    // Built by the sync from the bot's own hasTimeout / timeoutDuration /
    // followUpPrompt fields — not authored in this file.
    followup: z
      .object({
        enabled: z.boolean().optional(),
        followup_interval_in_seconds: z.number().optional(),
        message: z.string().optional(),
      })
      .catchall(z.unknown())
      .optional(),
    ai_audience: z.enum(['EVERYONE', 'ALLOWLISTED_ONLY']).optional(),
    // Numbers the agent may answer when ai_audience is ALLOWLISTED_ONLY. Rides
    // the normal settings sync — no separate allowlist endpoint.
    ai_audience_allowlist: z.array(z.string()).optional(),
    // Phrases the agent must never say — a Meta-side guardrail list that rides
    // the same settings PUT.
    never_say_phrases: z.array(z.string()).optional(),
  })
  .catchall(z.unknown());
export type MetaAgentSettings = z.infer<typeof metaAgentSettingsSchema>;

// Collection entries use Meta's real field names (per the official reference):
// skills match on `title`, FAQs on `question`, websites on `url`. Meta assigns
// the id; the key is what the reconciler matches on, so renaming an entry
// replaces it rather than editing it in place.
export const metaAgentSkillSchema = z
  .object({
    title: z.string().min(1),
    description: z.string().optional(),
    skill: z.string().optional(),
  })
  .catchall(z.unknown());

export const metaAgentFaqSchema = z
  .object({ question: z.string().min(1), answer: z.string().min(1) })
  .catchall(z.unknown());

export const metaAgentWebsiteSchema = z
  .object({ url: z.string().min(1) })
  .catchall(z.unknown());

// A knowledge file the agent reads. Meta takes an upload, not a link, so the
// file is fetched from `url` and pushed as multipart; `name` is what Meta shows
// and what the reconciler matches on, so it must be stable and unique.
export const metaAgentFileSchema = z
  .object({ url: z.string().min(1), name: z.string().min(1) })
  .catchall(z.unknown());

export type MetaAgentItem = { [key: string]: unknown };

// Desired state, authored in the org Lambda under `meta_ai_agent/<name>.json`
// and versioned with it. Reconciled onto Meta at activation and on deploy —
// Meta stays the live source of truth, this is only what we push. The agent's
// tools are NOT here: the file's `functions` key flows into the chatbot row's
// linked functions, exactly like every other bot type.
export const metaAgentConfigSchema = z
  .object({
    settings: metaAgentSettingsSchema.optional(),
    businessInfo: z.record(z.unknown()).optional(),
    skills: z.array(metaAgentSkillSchema).optional(),
    faqs: z.array(metaAgentFaqSchema).optional(),
    websites: z.array(metaAgentWebsiteSchema).optional(),
    files: z.array(metaAgentFileSchema).optional(),
  })
  .catchall(z.unknown());
export type MetaAgentConfig = z.infer<typeof metaAgentConfigSchema>;

// Per-business activation + sync bookkeeping. `syncedVersion` is the config
// hash last pushed to Meta, so a deploy can skip businesses already in sync.
export interface MetaAgentIntegration {
  onboarded?: boolean;
  agentId?: string;
  onboardedAt?: string;
  rolloutEnabled?: boolean;
  syncedVersion?: string;
  syncedAt?: string;
  syncError?: string;
  // Entries Meta 2xx-accepted but silently discarded on the last sync (async
  // content review) — the sync still succeeded, so this is a warning: shown in
  // the status panel and deploy response, and fed to the code-editor copilot
  // so it can rewrite the offending entries. Cleared when a sync lands clean.
  syncWarning?: string;
  // Id of the auto-provisioned connector API key. Rotation only ever
  // revokes/deletes THIS exact id — never a name match — so a user key can
  // never be swept by accident.
  connectorKeyId?: string;
  // Why the connector/tools push failed on THIS number, recorded on every sync
  // and cleared as soon as one succeeds. Tool support is being rolled out per
  // number, so the UI must warn only where it is actually missing.
  connectorError?: string;
  // When the connector was last actually attempted. Each attempt mints (and on
  // failure retires) an org:write key, so opening the diagnostics tab reuses a
  // recent result instead of churning credentials on every page view.
  connectorCheckedAt?: string;
  // HTTP status Meta answered the failed connector create with, so the panel
  // can show it as a code rather than burying it in the message text.
  connectorErrorStatus?: number;
}

// Collections the reconciler diffs: the Meta path and the field entries are
// matched on (verified against the official reference). Adding a collection
// here is the only change needed to sync it.
export const META_AGENT_COLLECTIONS = {
  skills: { path: '/agent_config/skills', key: 'title' },
  faqs: { path: '/agent_config/faq', key: 'question' },
  websites: { path: '/agent_config/websites', key: 'url' },
} as const;

export type MetaAgentCollection = keyof typeof META_AGENT_COLLECTIONS;

// Follow-up intervals Meta's agent_config/settings accepts, in seconds.
export const META_AGENT_FOLLOWUP_INTERVALS = [
  0, 300, 900, 1800, 3600, 7200, 28800, 86400,
] as const;

export const E164_REGEX = /^\+[1-9]\d{6,14}$/;

// ── FAQ markdown companion (`<agent>.faqs.md`) ───────────────────────────────
// Each `#`..`###` heading is a question; the section body below it is the
// answer. One file, any length, no JSON escaping — same reasoning as the
// chatbot's `.systemprompt.md`. Used by the editor (split/merge) AND the
// deploy extractor, so the two can never disagree on the format.

export interface FaqEntry {
  question: string;
  answer: string;
}

export const parseFaqsMarkdown = (md: string): FaqEntry[] => {
  const out: FaqEntry[] = [];
  let question: string | null = null;
  let buf: string[] = [];
  const flush = () => {
    const answer = buf.join('\n').trim();
    if (question && answer) out.push({ question, answer });
  };
  for (const line of md.split('\n')) {
    const m = line.match(/^#{1,3}\s+(.+?)\s*$/);
    if (m) {
      flush();
      question = m[1];
      buf = [];
    } else {
      buf.push(line);
    }
  }
  flush();
  return out;
};

export const serializeFaqsMarkdown = (faqs: FaqEntry[]): string =>
  faqs.map(f => `# ${f.question}\n\n${f.answer}`).join('\n\n') + '\n';
